/**
 * Custom Metrics - Edge Runtime Safe
 *
 * This file is safe to import from Edge Runtime (middleware)
 * because it doesn't import any Node.js modules.
 */

// Singleton no-op meters
const noopRecord = () => {};
const noopMeters = {
  externalApiDuration: { record: noopRecord },
  payloadApiDuration: { record: noopRecord },
};

// Cached meters - created once on first access
let cachedMeters: ReturnType<typeof createMeters> | null = null;
let cachedMeterProvider: any = null;

/**
 * Create histogram meters from the global MeterProvider
 */
function createMeters() {
  const meterProvider = (globalThis as any).__meterProvider;
  if (!meterProvider) {
    return noopMeters;
  }

  const meter = meterProvider.getMeter("marketing-website");

  return {
    externalApiDuration: meter.createHistogram("external_api_duration_seconds", {
      description: "External API call duration",
      unit: "s",
    }),
    payloadApiDuration: meter.createHistogram("payload_api_duration_seconds", {
      description: "Payload CMS API call duration",
      unit: "s",
    }),
  };
}

/**
 * Get custom meters - lazy initialization with module-level caching
 * Safe to call from Edge Runtime (returns no-ops if not in Node.js runtime)
 *
 * Caches meters after first creation to avoid repeatedly constructing histogram
 * objects and performing cache lookups on every request. Cache is invalidated
 * if the MeterProvider changes (e.g., in tests).
 */
export function getCustomMeters() {
  const currentMeterProvider = (globalThis as any).__meterProvider;

  // Invalidate cache if MeterProvider changed (e.g., in tests)
  if (currentMeterProvider !== cachedMeterProvider) {
    cachedMeters = null;
    cachedMeterProvider = currentMeterProvider;
  }

  // Return cached meters if already initialized
  if (cachedMeters) {
    return cachedMeters;
  }

  // Initialize and cache on first access
  cachedMeters = createMeters();
  return cachedMeters;
}

/**
 * Helper to measure and record async function execution time
 *
 * `labels` can be a plain object or a factory function evaluated after `fn`
 * resolves (useful when a label value like `status` is only known at runtime).
 *
 * @example
 * const result = await measureAsync(
 *   getCustomMeters().payloadApiDuration,
 *   { collection: "pages", locale: "en" },
 *   () => fetchFromPayload(...)
 * );
 *
 * @example — dynamic label via factory
 * let status = "success";
 * let cache: "hit" | "miss" = "hit";
 * await measureAsync(histogram, () => ({ collection, status, cache }), async () => {
 *   // status / cache are mutated by the fn or its callbacks before the factory is called
 * });
 */
export async function measureAsync<T>(
  histogram: { record: (value: number, labels: Record<string, string>) => void },
  labels: Record<string, string> | (() => Record<string, string>),
  fn: () => Promise<T>,
): Promise<T> {
  const start = performance.now();
  const resolveLabels = () => (typeof labels === "function" ? labels() : labels);
  try {
    const result = await fn();
    const duration = (performance.now() - start) / 1000;
    histogram.record(duration, resolveLabels());
    return result;
  } catch (error) {
    const duration = (performance.now() - start) / 1000;
    histogram.record(duration, resolveLabels());
    throw error;
  }
}

/**
 * Simplified metric measurement - single function for all metric types
 *
 * @example
 * const data = await measure("payloadApi", { collection: "pages", locale: "en" }, async () => {
 *   return fetchFromPayload(...);
 * });
 *
 * @example
 * const data = await measure("externalApi", { api: "xi", endpoint: "music/shared/songs/{slug}", status: "success" }, async () => {
 *   return fetch(...);
 * });
 */
export async function measure<T>(
  meter: "payloadApi" | "externalApi",
  labels: Record<string, string> | (() => Record<string, string>),
  fn: () => Promise<T>,
): Promise<T> {
  const meters = getCustomMeters();
  const histogramMap = {
    payloadApi: meters.payloadApiDuration,
    externalApi: meters.externalApiDuration,
  };

  return measureAsync(histogramMap[meter], labels, fn);
}

/**
 * Normalize URL path by replacing dynamic segments with placeholders
 * to avoid unbounded cardinality in metrics.
 *
 * Examples:
 * - /v1/music/shared/songs/abc123 -> /v1/music/shared/songs/{slug}
 * - /api/users/123/profile -> /api/users/{id}/profile
 * - /jobs/550e8400-e29b-41d4-a716-446655440000 -> /jobs/{uuid}
 */
export function normalizeUrlPath(pathname: string): string {
  // Match v1, v2, etc.
  const isApiUrl = /^\/?v[1-9]+/.test(pathname);

  return pathname.replace(/\/[^/]+/g, segment => {
    const value = segment.slice(1); // Remove leading slash

    // UUID pattern (8-4-4-4-12 hex digits)
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
      return "/{uuid}";
    }

    // MongoDB ObjectId pattern (24 hex digits)
    if (/^[0-9a-f]{24}$/i.test(value)) {
      return "/{id}";
    }

    // Numeric ID
    if (/^\d+$/.test(value)) {
      return "/{id}";
    }

    // Dynamic slug patterns - be conservative, only match things that really look like IDs:

    // 1. Very long segments (20+ chars) are likely dynamic — skip for API URLs
    //    to preserve static path components like "shared-sound-generations"
    if (!isApiUrl && value.length >= 20) {
      return "/{slug}";
    }

    // 2. Mixed letters and numbers that look random (e.g., "abc123def", "XNFt8yaoPjpHodk4G3pE")
    //    Must have both letters AND numbers, and be reasonably long.
    //    Applied even on API URLs — static API path components never mix letters with digits.
    const hasLetters = /[a-z]/i.test(value);
    const hasNumbers = /\d/.test(value);
    if (hasLetters && hasNumbers && value.length >= 12) {
      return "/{slug}";
    }

    // We are sure there are usually no dynamic slugs in API URLs beyond what's been checked above
    if (isApiUrl) {
      return segment;
    }

    // 3. Multiple hyphens or underscores often indicate slugified titles
    //    (e.g., "my-blog-post-title", "some_generated_slug")
    const hyphenCount = (value.match(/-/g) || []).length;
    const underscoreCount = (value.match(/_/g) || []).length;
    if ((hyphenCount >= 2 || underscoreCount >= 2) && value.length >= 15) {
      return "/{slug}";
    }

    // Keep the segment as-is (likely a static path component)
    return segment;
  });
}

/**
 * Measured fetch - automatically tracks external API calls
 *
 * Extracts API name and endpoint from URL and records metrics automatically.
 * URLs are normalized to replace dynamic segments (IDs, slugs, UUIDs) with
 * placeholders to avoid unbounded cardinality.
 *
 * @example
 * const response = await measuredFetch("https://api.ashbyhq.com/jobPosting.list", options);
 * const data = await response.json();
 */
export async function measuredFetch(url: string, options?: RequestInit): Promise<Response> {
  const start = performance.now();

  // Parse URL to extract hostname and endpoint
  const urlObj = new URL(url);
  const api = urlObj.hostname;
  const endpoint = normalizeUrlPath(urlObj.pathname).replace(/^\//, ""); // Remove leading slash

  try {
    const response = await fetch(url, options);
    const duration = (performance.now() - start) / 1000;

    getCustomMeters().externalApiDuration.record(duration, {
      api,
      endpoint,
      status: response.ok ? "success" : "error",
    });

    return response;
  } catch (error) {
    const duration = (performance.now() - start) / 1000;

    getCustomMeters().externalApiDuration.record(duration, {
      api,
      endpoint,
      status: "error",
    });

    throw error;
  }
}
